import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/Button";
import { useCancelBillingItemMutation } from "../../../query/billingItemsQuery";
import { toDateString, formatDate } from "../../../utils/dateUtils";
import {
  getMinimumActiveUntilDate,
  isDateBeforeMinimumActiveUntil,
  isEndDateBeforePaidUntil,
} from "../../../utils/billingItemMinimumActive";
import type { AvailableBillingItemId, StoreId } from "@KassaroAB/shared/types/ids";
import type { BillingItemType } from "@KassaroAB/shared/types/enums/BillingItemType";

interface BillingItemEndButtonProps {
  availableBillingItemId: AvailableBillingItemId;
  billingItemType: BillingItemType;
  activeSince: string;
  paidUntil: string | null;
  isAdmin: boolean;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  disabled?: boolean;
}

export const BillingItemEndButton = ({
  availableBillingItemId,
  billingItemType,
  activeSince,
  paidUntil,
  isAdmin,
  onSuccess,
  onError,
  disabled = false,
}: BillingItemEndButtonProps) => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const { mutate: cancelBillingItem, isPending } =
    useCancelBillingItemMutation(storeId ?? null);
  const effectiveFrom = toDateString(new Date());
  const minimumDate = getMinimumActiveUntilDate(billingItemType, activeSince);
  const endsBeforeMinimum = isDateBeforeMinimumActiveUntil(
    billingItemType,
    activeSince,
    effectiveFrom
  );
  const endsBeforePaidUntil =
    !isAdmin && isEndDateBeforePaidUntil(effectiveFrom, paidUntil);

  return (
    <Button
      type="button"
      $color="secondary"
      disabled={disabled || isPending}
      onClick={() => {
        if (endsBeforePaidUntil && paidUntil) {
          onError?.(
            new Error(
              t("billingItems.edit.endDateBeforePaidUntil", {
                paidUntilDate: formatDate(paidUntil),
              })
            )
          );
          return;
        }
        const confirmMessage = endsBeforeMinimum && minimumDate
          ? t("billingItems.edit.cancelBeforeMinimum", {
              minimumDate: formatDate(minimumDate),
            })
          : t("billingItems.actions.cancelConfirm");
        if (!window.confirm(confirmMessage)) {
          return;
        }
        cancelBillingItem(
          {
            availableBillingItemId,
            effectiveFrom,
          },
          { onSuccess, onError }
        );
      }}
    >
      {isPending
        ? t("billingItems.actions.cancelling")
        : t("billingItems.actions.cancel")}
    </Button>
  );
};
